import { DestroyRef, inject, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { select, Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';

import {
  DialogService,
  DialogCloseOptions,
  DialogCloseReason,
} from '@sui/dialog';

import {
  ObjectsData,
  FeedDuplicateDialogComponent,
} from '../components/popups/feed-duplicate-dialog/feed-duplicate-dialog.component';
import { ToolbarButton } from '../models/toolbar.models';
import { MoveObjectDialogComponent } from '../components/popups/move-object-dialog/move-object-dialog.component';
import { ConfirmDialogComponent } from '../components/popups/confirm-dialog/confirm-dialog.component';
import { ObjectPermissionsDialogComponent } from '../components/popups/object-permissions-dialog/object-permissions-dialog.component';
import { WorkspaceDuplicateDialogComponent } from '../components/popups/workspace-duplicate-dialog/workspace-duplicate-dialog.component';
import { PickerDataobjectsDialogComponent } from '../components/popups/picker-dataobjects-dialog/picker-dataobjects-dialog.component';

import {
  PopupType,
  WidgetType,
  NomObjectClass,
  NomObjectTypes,
  MassOperationType,
  NomObjectLifecycleStatus,
} from '../constants';
import { DataObject, ObjectDefinition } from '../models/core.models';

import {
  toggleObjectSelected,
  clearSelectedObjects,
} from '../actions/selected.actons';
import {
  deleteWidgets,
  deleteWidgetsFail,
  deleteWidgetsSuccess,
} from '../actions/widget.actions';
import { injectDeleteWidgets } from 'src/app/api/queries/widget.queries';
import { WidgetPersistenceService } from './widget-persistence.service';
import { massObjectOperationSuccess } from '../actions/core.actions';
import { injectMassObjectOperation } from 'src/app/api/queries/object.queries';

import { AppState } from 'src/app/reducers';
import { selectSelectedObjects } from '../reducers';

import { tap, take, filter } from 'rxjs';

export interface ObjectAction {
  copy?: boolean;
  move?: boolean;
  grant_permissions?: boolean;
  link_dimensions?: boolean;
  archive?: boolean;
  unarchive?: boolean;
  delete?: boolean;
  undelete?: boolean;
  attach_filters?: boolean;
  attach_pivots?: boolean;
  detach_filters?: boolean;
  detach_pivots?: boolean;
}

type LifecycleStatusActions = {
  [key in NomObjectLifecycleStatus]: Partial<
    Record<NomObjectTypes, ObjectAction>
  >;
};

type ObjectActions = {
  [key in NomObjectClass]?: Partial<LifecycleStatusActions>;
};

@Injectable({
  providedIn: 'root',
})
export class ObjectActionsService {
  public selectedObjects: Map<string, DataObject>;

  private destroyRef = inject(DestroyRef);

  // * imperative, not a mutation hook: this is a root service (api-layer.md's
  // * consumer rule)
  private readonly deleteWidgetsRequest = injectDeleteWidgets();
  private readonly runMassObjectOperation = injectMassObjectOperation();
  private readonly widgetPersistence = inject(WidgetPersistenceService);

  public selectMode: boolean = false;
  objectActions: ObjectActions = {};
  availableMassOperations: any = {};
  objectActionPermissionIdMap: Record<string, number>;
  featurePermissionIdMap: Record<string, string>;

  private dialogService = inject(DialogService);

  constructor(
    private store: Store<AppState>,
    private translateService: TranslateService,
  ) {
    this.store
      .pipe(
        select(selectSelectedObjects),
        tap((selectedObjects) => {
          this.selectedObjects = selectedObjects;
          this.selectMode = selectedObjects.size > 0;
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();

    this.objectActions[NomObjectClass.WORKSPACE] = {
      [NomObjectLifecycleStatus.ACTIVE]: {
        [NomObjectTypes.WORKSPACE_TYPE_FEEDS]: {
          copy: true,
          grant_permissions: true,
          archive: true,
          delete: true,
        },
        [NomObjectTypes.WORKSPACE_TYPE_CATALOG]: {
          copy: true,
          grant_permissions: true,
          archive: true,
          unarchive: true,
          delete: true,
          undelete: true,
        },
      },
      [NomObjectLifecycleStatus.ARCHIVED]: {
        [NomObjectTypes.WORKSPACE_TYPE_FEEDS]: {
          unarchive: true,
        },
        [NomObjectTypes.WORKSPACE_TYPE_CATALOG]: {
          unarchive: true,
        },
      },
      [NomObjectLifecycleStatus.DELETED]: {
        [NomObjectTypes.WORKSPACE_TYPE_FEEDS]: {
          undelete: true,
        },
        [NomObjectTypes.WORKSPACE_TYPE_CATALOG]: {
          undelete: true,
        },
      },
    };

    this.objectActions[NomObjectClass.DASHBOARD] = {
      [NomObjectLifecycleStatus.ACTIVE]: {
        [NomObjectTypes.DASHBOARD_TYPE]: {
          grant_permissions: true,
          archive: true,
          delete: true,
        },
        [NomObjectTypes.DASHBOARD]: {
          grant_permissions: true,
          archive: true,
          delete: true,
        },
      },

      [NomObjectLifecycleStatus.ARCHIVED]: {
        [NomObjectTypes.DASHBOARD_TYPE]: {
          unarchive: true,
        },
        [NomObjectTypes.DASHBOARD]: {
          unarchive: true,
        },
      },
      [NomObjectLifecycleStatus.DELETED]: {
        [NomObjectTypes.DASHBOARD_TYPE]: {
          undelete: true,
        },
        [NomObjectTypes.DASHBOARD]: {
          undelete: true,
        },
      },
    };

    this.objectActions[NomObjectClass.FEED] = {
      [NomObjectLifecycleStatus.ACTIVE]: {
        [NomObjectTypes.ONLINE_FEED]: {
          copy: true,
          move: true,
          grant_permissions: true,
          //link_dimensions: true,
          archive: true,
          delete: true,
        },
        [NomObjectTypes.CLIPPING_FEED]: {
          copy: true,
          move: true,
          grant_permissions: true,
          //link_dimensions: true,
          archive: true,
          delete: true,
        },
        [NomObjectTypes.SOCIAL_FEED]: {
          copy: true,
          move: true,
          grant_permissions: true,
          //link_dimensions: true,
          archive: true,
          delete: true,
        },
        [NomObjectTypes.COMBINED_FEED]: {
          copy: true,
          move: true,
          grant_permissions: true,
          //link_dimensions: true,
          archive: true,
          delete: true,
        },
        [NomObjectTypes.SEARCH_DIMENSION]: {
          copy: true,
          move: true,
          grant_permissions: true,
          archive: true,
          delete: true,
        },
        [NomObjectTypes.SOURCE_CATALOG_FEED]: {
          copy: true,
          move: true,
          grant_permissions: true,
          //link_dimensions: true,
          archive: true,
          delete: true,
        },
        [NomObjectTypes.BROADCAST_FEED]: {
          copy: true,
          move: true,
          grant_permissions: true,
          //link_dimensions: true,
          archive: true,
          delete: true,
        },
        [NomObjectTypes.STORY_FEED]: {
          copy: true,
          move: true,
          grant_permissions: true,
          archive: true,
          delete: true,
        },
      },
      [NomObjectLifecycleStatus.ARCHIVED]: {
        [NomObjectTypes.ONLINE_FEED]: {
          unarchive: true,
        },
        [NomObjectTypes.CLIPPING_FEED]: {
          unarchive: true,
        },
        [NomObjectTypes.SOCIAL_FEED]: {
          unarchive: true,
        },
        [NomObjectTypes.COMBINED_FEED]: {
          unarchive: true,
        },
        [NomObjectTypes.SEARCH_DIMENSION]: {
          unarchive: true,
        },
        [NomObjectTypes.SOURCE_CATALOG_FEED]: {
          unarchive: true,
        },
        [NomObjectTypes.BROADCAST_FEED]: {
          unarchive: true,
        },
        [NomObjectTypes.STORY_FEED]: {
          unarchive: true,
        },
      },
      [NomObjectLifecycleStatus.DELETED]: {
        [NomObjectTypes.ONLINE_FEED]: {
          undelete: true,
        },
        [NomObjectTypes.CLIPPING_FEED]: {
          undelete: true,
        },
        [NomObjectTypes.SOCIAL_FEED]: {
          undelete: true,
        },
        [NomObjectTypes.COMBINED_FEED]: {
          undelete: true,
        },
        [NomObjectTypes.SEARCH_DIMENSION]: {
          undelete: true,
        },
        [NomObjectTypes.SOURCE_CATALOG_FEED]: {
          undelete: true,
        },
        [NomObjectTypes.BROADCAST_FEED]: {
          undelete: true,
        },
        [NomObjectTypes.STORY_FEED]: {
          undelete: true,
        },
      },
    };

    this.objectActions[NomObjectClass.WIDGET] = {
      [NomObjectLifecycleStatus.ACTIVE]: {
        [NomObjectTypes.UNKNOWN]: {
          copy: true,
          attach_filters: true,
          attach_pivots: true,
          detach_filters: true,
          detach_pivots: true,
          delete: true,
        },
      },
    };

    this.objectActionPermissionIdMap = {
      copy: 1, // Placeholder
      move: 2, // Placeholder
      grant_permissions: 3, // Placeholder
      link_dimensions: 4, // Placeholder
      archive: 5, // Placeholder
      unarchive: 6, // Placeholder
      delete: 7, // Placeholder
      undelete: 8, // Placeholder
    };

    this.featurePermissionIdMap = {
      copy: 'NOM_APP_CMP_OBJECT_APPLICABLE_ACTIONS_COPY_OBJECT', // Placeholder
      move: 'NOM_APP_CMP_OBJECT_APPLICABLE_ACTIONS_MOVE_OBJECT', // Placeholder
      grant_permissions:
        'NOM_APP_CMP_OBJECT_APPLICABLE_ACTIONS_GRANT_PERMISSIONS', // Placeholder
    };
  }

  toggleObjectSelection(object: DataObject): void {
    this.store.dispatch(toggleObjectSelected({ object }));
  }

  getButtonForAction(action: string): ToolbarButton {
    const actionMap = {
      copy: {
        label: this.translateService.instant('btn.copy-or-move'),
        icon: ['fas', 'copy'],
      },
      move: {
        label: this.translateService.instant('btn.move'),
        icon: ['fas', 'arrows-alt'],
      },
      grant_permissions: {
        label: this.translateService.instant(
          'object-actions.label.grant-permission',
        ),
        icon: ['fas', 'user-shield'],
      },
      link_dimensions: {
        label: this.translateService.instant(
          'object-actions.label.link-dimension',
        ),
        icon: ['fas', 'link'],
      },
      archive: {
        label: this.translateService.instant('object-actions.label.archive'),
        icon: ['fas', 'archive'],
      },
      unarchive: {
        label: this.translateService.instant('object-actions.label.unarchive'),
        icon: ['fas', 'unarchive'],
      },
      delete: {
        label: this.translateService.instant('btn.delete'),
        icon: ['fas', 'times'],
      },
      undelete: {
        label: this.translateService.instant('object-actions.label.undelete'),
        icon: ['fas', 'trash-restore'],
      },
      attach_pivots: {
        label: this.translateService.instant(
          'object-actions.label.attach-pivots',
        ),
        icon: ['fas', 'layer-group'],
      },
      detach_pivots: {
        label: this.translateService.instant(
          'object-actions.label.detach-pivots',
        ),
        icon: ['fas', 'file-circle-minus'],
      },
      attach_filters: {
        label: this.translateService.instant(
          'object-actions.label.attach-filters',
        ),
        icon: ['fas', 'filter'],
      },
      detach_filters: {
        label: this.translateService.instant(
          'object-actions.label.detach-filters',
        ),
        icon: ['fas', 'filter-circle-xmark'],
      },
    };

    const actionItem = actionMap[action];

    return {
      type: 'button',
      label: actionItem.label,
      variant: 'secondary',
      leadingIcon: actionItem.icon,
      action: () => {
        this.performAction(action, this.selectedObjects);
      },
    };
  }

  move() {
    this.dialogService.openSimpleDialog(MoveObjectDialogComponent, {
      data: {
        objects: Array.from(this.selectedObjects.values()),
      },
      closeOnClick: false,
      closeOnEscape: true,
      width: '25vw',
      height: '25vh',
      minWidth: 300,
      minHeight: 300,
      maxWidth: 500,
      maxHeight: 500,
    });
  }

  isObjectSelected(object: DataObject): boolean {
    return this.selectedObjects.has(object.id);
  }

  clearObjectSelection(): void {
    this.store.dispatch(clearSelectedObjects());
  }

  grantPermissions() {
    this.dialogService.openTabsDialog(ObjectPermissionsDialogComponent, {
      data: {
        objects: Array.from(this.selectedObjects.values()),
        validateFormsOnInit: true,
      },
      closeOnClick: false,
      closeOnEscape: true,
      width: '50vw',
      height: '70vh',
      minWidth: 600,
      minHeight: 800,
      maxWidth: 920,
      maxHeight: 1020,
    });
  }

  /**
   * Retrieves the unique class ID(s) of the selected objects.
   * If there is only one unique class ID, it returns that ID.
   * If there are multiple unique class IDs, it returns an array of those IDs.
   * @returns The unique class ID(s) of the selected objects.
   */
  getUniqueClassId() {
    const objectClassIds = Array.from(this.selectedObjects.values()).map(
      (object) => {
        let objectClassId = object.object_class_id;
        // widgets are a special case, object_class_id and object_id are set to the same value
        if (object['widget_type_id'] !== undefined) {
          objectClassId = NomObjectClass.WIDGET;
        }

        return objectClassId;
      },
    );
    const uniqueObjectClassIds = [...new Set(objectClassIds)];
    if (uniqueObjectClassIds.length > 1) {
      return uniqueObjectClassIds;
    }

    return uniqueObjectClassIds[0];
  }

  cloneObjects() {
    const uniqueObjectClassId = this.getUniqueClassId();
    let objectsData: ObjectsData[] = [];
    this.selectedObjects.forEach((object) => {
      objectsData.push({ id: object.id, classId: object.object_class_id });
    });

    // check if is array
    if (Array.isArray(uniqueObjectClassId)) {
      alert('Multiple object types selected');
      return;
    }

    switch (uniqueObjectClassId) {
      case NomObjectClass.FEED:
        this.dialogService.openTabsDialog(FeedDuplicateDialogComponent, {
          data: {
            objectsData: objectsData as ObjectsData[],
            validateFormsOnInit: true,
          },
          closeOnClick: false,
          closeOnEscape: true,
          width: '50vw',
          height: '50vh',
          minWidth: 800,
          minHeight: 800,
          maxWidth: 1020,
          maxHeight: 1020,
        });

        break;
      case NomObjectClass.WORKSPACE:
        this.dialogService.openTabsDialog(WorkspaceDuplicateDialogComponent, {
          data: {
            objectsData: objectsData,
          },
          closeOnClick: false,
          closeOnEscape: true,
          width: '50vw',
          height: '50vh',
          minWidth: 800,
          minHeight: 800,
          maxWidth: 1020,
          maxHeight: 1020,
        });
        break;
      default:
        break;
    }
  }

  performAction(
    action: string,
    selectedObjects: Map<string, DataObject>,
  ): void {
    // Perform the action here
    // console.log(
    //   'Performing action',
    //   action,
    //   'on selected objects',
    //   selectedObjects,
    // );

    switch (action) {
      case 'copy':
        // Do something
        this.cloneObjects();
        break;
      case 'move':
        this.move();
        break;
      case 'grant_permissions':
        // Do something
        this.grantPermissions();
        break;
      case 'link_dimensions':
        // Do something
        break;
      case 'archive':
        // Do something
        this.performOperation('archive');
        break;
      case 'unarchive':
        // Do something
        this.performOperation('unarchive');
        break;

      case 'delete':
        // Do something
        const uniqueObjectClassId = this.getUniqueClassId();

        switch (uniqueObjectClassId) {
          case NomObjectClass.WIDGET:
            this.deleteWidgets();
            break;
          default:
            this.performOperation('delete');
            break;
        }

        break;
      case 'undelete':
        // Do something
        this.performOperation('undelete');
        break;
      case 'attach_filters':
        this.attachFilters();
        break;
      case 'detach_filters':
        this.detachFilters();
        break;
      case 'attach_pivots':
        this.attachPivots();
        break;
      case 'detach_pivots':
        this.detachPivots();
        break;
      default:
        break;
    }
  }

  private deleteWidgets() {
    const message = 'Are you sure you want to delete the selected widgets?';

    const dialogRef = this.dialogService.openSimpleDialog(
      ConfirmDialogComponent,
      {
        data: {
          message,
        },
        closeOnClick: false,
      },
    );

    dialogRef.closed.subscribe(
      (event: DialogCloseOptions<{ reason: DialogCloseReason }>) => {
        if (event.reason === DialogCloseReason.Confirm) {
          const objectIds = Array.from(this.selectedObjects.values()).map(
            (object) => {
              return object.id;
            },
          );

          this.removeWidgets(objectIds);
        } else if (event.reason === DialogCloseReason.Cancel) {
        }
      },
    );
  }

  /**
   * The trigger action raises the `loadingWidgets` spinner; the Success / Fail
   * broadcasts drop the rows from the cached list and lower it again.
   */
  private removeWidgets(widgetIds: string[]) {
    this.store.dispatch(deleteWidgets({ widgetIds }));

    this.deleteWidgetsRequest(widgetIds)
      .then(() => this.store.dispatch(deleteWidgetsSuccess({ widgetIds })))
      .catch((error) => this.store.dispatch(deleteWidgetsFail({ error })));
  }

  private getSelectedWidgetIds(): string[] {
    return Array.from(this.selectedObjects.values())
      .filter(
        (obj) => obj['widget_type_id'] !== WidgetType.WIDGET_TYPE_SECTION_BREAK,
      )
      .map((obj) => obj.id);
  }

  // * `settingsKey` is the widget `settings` field the picked ids land in;
  // * WidgetPersistenceService builds and PATCHes the payload
  private attachDimensions(settingsKey: string, popupType: PopupType) {
    const widgetIds = this.getSelectedWidgetIds();

    const dialogRef = this.dialogService.openTabsDialog(
      PickerDataobjectsDialogComponent,
      {
        data: {
          objectTypeIds: [NomObjectTypes.SEARCH_DIMENSION],
          popupType,
        },
        width: '61vw',
        minWidth: 1220,
        maxWidth: 1550,
        height: '70vh',
        closeOnClick: false,
        closeOnEscape: true,
      },
    );

    dialogRef.closed
      .pipe(
        take(1),
        filter(
          (event: any) =>
            event?.reason === DialogCloseReason.Save ||
            event?.reason === DialogCloseReason.SaveAndClose,
        ),
      )
      .subscribe((event: any) => {
        this.widgetPersistence.applySettings(widgetIds, {
          [settingsKey]: event.data,
        });
      });
  }

  private detachEntities(messageKey: string, settingsKey: string) {
    const widgetIds = this.getSelectedWidgetIds();
    const message = this.translateService.instant(messageKey);

    const dialogRef = this.dialogService.openSimpleDialog(
      ConfirmDialogComponent,
      {
        data: {
          message,
        },
        closeOnClick: false,
      },
    );

    dialogRef.closed.subscribe(
      (event: DialogCloseOptions<{ reason: DialogCloseReason }>) => {
        if (event.reason === DialogCloseReason.Confirm) {
          this.widgetPersistence.applySettings(widgetIds, {
            [settingsKey]: [],
          });
        } else if (event.reason === DialogCloseReason.Cancel) {
          console.log(`Deletion cancelled: ${event.reason}`);
        }
      },
    );
  }

  private attachFilters() {
    this.attachDimensions(
      'dimension_id_filter',
      PopupType.ATTACH_DIMENSIONS_AS_FILTERS,
    );
  }

  private detachFilters() {
    this.detachEntities(
      'widget-actions.message.detach-filters',
      'dimension_id_filter',
    );
  }

  private attachPivots() {
    this.attachDimensions(
      'dimension_id_pivot',
      PopupType.ATTACH_DIMENSIONS_AS_PIVOTS,
    );
  }

  private detachPivots() {
    this.detachEntities(
      'widget-actions.message.detach-pivot',
      'dimension_id_pivot',
    );
  }

  private performOperation(operation: MassOperationType) {
    const message = this.translateService.instant(
      'object-actions.msg.are-you-sure-you-want-to-run-operation-on-the-object',
      { operation },
    );

    const dialogRef = this.dialogService.openSimpleDialog(
      ConfirmDialogComponent,
      {
        data: {
          message,
        },
        closeOnClick: false,
      },
    );

    dialogRef.closed.subscribe(
      (event: DialogCloseOptions<{ reason: DialogCloseReason }>) => {
        if (event.reason === DialogCloseReason.Confirm) {
          const objects = Array.from(this.selectedObjects.values());

          const objectDefs: ObjectDefinition[] = objects.map((object) => {
            return {
              object_id: object.id,
              object_class_id: object.object_class_id,
              object_type_id: object.object_type_id,
            };
          });

          this.runMassObjectOperation(operation, objectDefs)
            .then((response) =>
              this.store.dispatch(massObjectOperationSuccess({ response })),
            )
            .catch(() => undefined);
        } else if (event.reason === DialogCloseReason.Cancel) {
          console.log(`Action cancelled: ${event.reason}`);
        }
      },
    );
  }
}
